import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Empty, relTime } from '../../components/ui.jsx'
import { useAyuda } from '../../lib/ayuda.jsx'
import { AyudaFuncionesLista } from '../../components/AyudaContrato.jsx'

// Convenio de la plataforma: el nombre de la función es una CARPETA dentro de functions/ en el
// repositorio, así que tiene que ser un nombre de carpeta válido y plano. Sin barras: agrupar se
// hace con guiones. Se aplica aquí para no crear hoy funciones que mañana no puedan versionarse.
const NOMBRE = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/

export function Functions() {
  // La parte del contrato que toca a esta página, en el panel de ayuda («?» de la cabecera).
  useAyuda(() => ({ titulo: 'Funciones · el contrato', nodo: <AyudaFuncionesLista /> }), [])

  const { slug } = useParams()
  const nav = useNavigate()
  const [creando, setCreando] = useState(false)
  const [nombre, setNombre] = useState('')
  const [lenguaje, setLenguaje] = useState('bun')

  const qc = useQueryClient()
  const lista = useQuery({ queryKey: ['functions', slug], queryFn: () => api.functions(slug) })
  const sincronizar = useMutation({
    mutationFn: () => api.syncFns(slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['functions', slug] }),
  })
  const langs = useQuery({ queryKey: ['languages'], queryFn: api.languages, staleTime: Infinity })

  const nombreOk = NOMBRE.test(nombre)
  const crear = (e) => {
    e.preventDefault()
    if (!nombreOk) return
    nav(`/p/${slug}/functions/edit?path=${encodeURIComponent(`f/${slug}/${nombre}`)}&lang=${lenguaje}&nueva=1`)
  }

  const nombreDe = (l) => langs.data?.find((x) => x.id === l)?.nombre ?? l

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">Funciones</h1>
          <p className="lede">
            Scripts de Windmill del proyecto. Viven en <code>f/{slug}/</code> y solo este proyecto los ve.
          </p>
        </div>
        <div className="toolbar">
          <button className="btn secondary" disabled={sincronizar.isPending}
                  title="Vuelve a publicar todo lo que haya en functions/ del repositorio"
                  onClick={() => sincronizar.mutate()}>
            {sincronizar.isPending ? 'Sincronizando…' : 'Sincronizar desde git'}
          </button>
          <button className="btn primary" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nueva función'}
          </button>
        </div>
      </div>

      {creando && (
        <form className="card card-pad" onSubmit={crear} style={{ marginBottom: 18 }}>
          <div className="field-label">Nombre</div>
          <div className="toolbar">
            <span className="hint" style={{ fontFamily: 'var(--mono)', marginLeft: 0 }}>f/{slug}/</span>
            <input className="input" autoFocus value={nombre} placeholder="limpieza-diaria"
                   onChange={(e) => setNombre(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
            <select className="input" value={lenguaje} onChange={(e) => setLenguaje(e.target.value)}>
              {(langs.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
            <button className="btn primary" disabled={!nombreOk}>Crear</button>
          </div>
          <div className="field-hint">
            Letras, dígitos, <code>-</code> y <code>_</code>. Sin espacios ni barras: en el repositorio
            será la carpeta <code>functions/{nombre || 'nombre'}/</code>.
          </div>
        </form>
      )}

      {sincronizar.data && (
        <div className={`alert ${sincronizar.data.errores?.length ? 'warn' : ''}`} style={{ marginBottom: 16 }}>
          {sincronizar.data.publicadas?.length ?? 0} publicadas ·{' '}
          {sincronizar.data.saltadas?.length ?? 0} sin cambios
          {sincronizar.data.errores?.length > 0 && (
            <ul style={{ margin: '8px 0 0 18px' }}>
              {sincronizar.data.errores.map((e) => <li key={e.nombre}>{e.nombre}: {e.error}</li>)}
            </ul>
          )}
          {(sincronizar.data.publicadas?.length ?? 0) === 0 &&
           (sincronizar.data.saltadas?.length ?? 0) === 0 && (
            <> — no hay nada bajo <code>functions/</code> en la rama desplegada.</>
          )}
        </div>
      )}
      {sincronizar.isError && (
        <div className="alert error" style={{ marginBottom: 16 }}>
          {sincronizar.error.body?.detail ?? sincronizar.error.message}
        </div>
      )}

      {lista.isPending && <Spinner />}
      {lista.isError && (
        <div className="alert error">
          No se pudo hablar con Windmill · <code>{lista.error.body?.detail ?? lista.error.message}</code>
        </div>
      )}

      {lista.data?.length === 0 && (
        <Empty title="Todavía no hay funciones">
          Una función es un script que se ejecuta bajo demanda o programado, sin desplegar nada.
        </Empty>
      )}

      {lista.data?.length > 0 && (
        <div className="card"><div className="table-scroll">
          <table className="t">
            <thead><tr><th>Función</th><th>Lenguaje</th><th>Autor</th><th>Modificada</th></tr></thead>
            <tbody>
              {lista.data.map((f) => (
                <tr key={f.path}>
                  <td>
                    <Link to={`/p/${slug}/functions/edit?path=${encodeURIComponent(f.path)}`}>
                      {f.path.replace(`f/${slug}/`, '')}
                    </Link>
                    {f.summary && <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>{f.summary}</div>}
                  </td>
                  <td>{nombreDe(f.language)}</td>
                  <td className="ellipsis">{f.createdBy}</td>
                  <td>{relTime(f.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
    </>
  )
}
